import React, { useState, useEffect } from 'react';
import { ContentBoxContainer, ContentBoxText, ContentBoxFigure, Spacer, Decoline, PenlySeo, Button, Query, Tag } from 'components';
import { Routes } from 'utils/routes';
import BLOG_ENTRIES_QUERY from 'queries/blog/blogEntries';
import BLOG_CATEGORIES_QUERY from 'queries/blog/blogCategories';
import { getEventDate } from 'utils/blogHelpers';
import { useQuery } from '@apollo/client';

// Date formatting
import dateFormat from 'date-fns/format';
import isDateAfter from 'date-fns/isAfter';
import { da } from 'date-fns/locale';

export default function Blog() {
    const title = 'Penly - Tag ejerskab over din økonomi';
    const description = 'Bliv klogere på din økonomi med Penlys artikler og guides, inden for pension, investeringer, budget, lån og rådgivning.';
    const image = { url: '/static/gfx/content/penly-content-8.jpg' };
    const locale = { locale: da };
    const [category, setCategory] = useState();
    const [categories, setCategories] = useState([]);

    const getEventUrl = (blogEntry) => {
        if (blogEntry.event_url) {
            if (blogEntry.event_url.indexOf('http') === -1) {
                return `http://${blogEntry.event_url}`;
            }

            return blogEntry.event_url;
        }

        return Routes.Knowledge.Entry(blogEntry.slug);
    };

    const getWebinarTag = (blogEntry) => {
        if (blogEntry.event_date_start) {
            const dateStart = new Date(blogEntry.event_date_start);
            return isDateAfter(new Date(), dateStart) ? 'Afholdt webinar' : 'Webinar';
        }

        return 'Webinar';
    };

    const getCategoryAndTags = (blogEntry) => (
        <>
            { blogEntry.blog_category &&
            <Tag>{ blogEntry.blog_category.title }</Tag>
            }

            { (blogEntry.tags && blogEntry.tags.length > 0) && blogEntry.tags.map((tag) => (
                <Tag key={ tag.id }>{ tag.name }</Tag>
            )) }
        </>
    );

    const filterBlogEntries = (blogEntries) => {
        const entries = [];

        blogEntries.forEach(entry => {
            if (category === undefined || (entry.blog_category && entry.blog_category.title.toLowerCase() === category.toLowerCase())) {
                entries.push(entry);
            }
        });

        return entries;
    };

    const { data } = useQuery(BLOG_CATEGORIES_QUERY, {
        variables: null,
        ssr: true,
    });

    useEffect(() => {
        if (data && data.blogCategories && data.blogCategories.length > 0) {
            const filteredCategories = [];

            data.blogCategories.forEach(categoryValue => {
                if (categoryValue.blog_entries && categoryValue.blog_entries.length > 0) {
                    filteredCategories.push(categoryValue);
                }
            });

            setCategories(filteredCategories);
        }
    }, [data]);

    return (
        <>
            <PenlySeo title={ title } description={ description } image={ image } url={ Routes.Knowledge.LandingPage } />
            <ContentBoxContainer className="fluid">
                <ContentBoxText>
                    <h1 className="nomb">
                        Events <span className="color-highlight">og</span> webinarer
                    </h1>
                    <br />
                    <p className="large">Deltag i vores events og bliv klogere på din økonomi - uden sort snak.</p>
                    <Decoline className="large" />
                    <div className="tags clickable">
                        { categories && category && <Tag color="secondary" onClick={ () => setCategory(undefined) }>Alle</Tag> }
                        {
                            categories && categories.map((categoryValue) => (
                                <Tag key={ categoryValue.id } onClick={ () => setCategory(categoryValue.title) } color={ category=== categoryValue.title ? 'highlight': '' }>{ categoryValue.title }</Tag>
                            ))
                        }
                    </div>
                </ContentBoxText>
                <ContentBoxFigure className="penlylogoSymbol"></ContentBoxFigure>
            </ContentBoxContainer>

            <Query query={ BLOG_ENTRIES_QUERY }>
                { ({ data }) => (
                    <>
                        { filterBlogEntries(data.blogEntries).map((blogEntry, index) => (
                            <React.Fragment key={ index }>
                                <ContentBoxContainer className="fluid blogEntryIntro leftPriority mobileflip tabletflip">
                                    <ContentBoxText>
                                        {
                                            blogEntry.blog_type === 'Event' && blogEntry.event_date_start &&
                                            <>
                                                <h2 className="nomb">
                                                    <a href={ Routes.Knowledge.Entry(blogEntry.slug) }>{ blogEntry.title }</a>
                                                </h2>
                                                <div className="tags"><Tag color="highlight">Event</Tag>{ getCategoryAndTags(blogEntry) }</div>
                                                <div className="date">{ getEventDate(blogEntry) }</div>
                                            </>
                                        }
                                        {
                                            blogEntry.blog_type === 'Webinar' && blogEntry.event_date_start &&
                                            <>
                                                <h2 className="nomb">
                                                    <a href={ getEventUrl(blogEntry) }>{ blogEntry.title }</a>
                                                </h2>
                                                <div className="tags"><Tag color="highlight">{ getWebinarTag(blogEntry) }</Tag>{ getCategoryAndTags(blogEntry) }</div>
                                                <div className="date">{ getEventDate(blogEntry) }</div>
                                            </>
                                        }
                                        {
                                            (blogEntry.blog_type === 'Article' || blogEntry.blog_type === 'Blog') &&
                                            <>
                                                <h2 className="nomb">
                                                    <a href={ Routes.Knowledge.Entry(blogEntry.slug) }>{ blogEntry.title }</a>
                                                </h2>
                                                <div className="tags">{ getCategoryAndTags(blogEntry) }</div>
                                                <div className="date">{ dateFormat(new Date(blogEntry.published_at), 'PPP', locale) }</div>
                                            </>
                                        }
                                        <br />
                                        <p>
                                            { blogEntry.summary }
                                        </p>
                                        <Spacer />
                                        {
                                            blogEntry.blog_type === 'Event' &&
                                            <div>
                                                <Button href={ Routes.Knowledge.Entry(blogEntry.slug) }>Læs om eventen</Button>
                                            </div>
                                        }
                                        {
                                            blogEntry.blog_type === 'Webinar' &&
                                            <div>
                                                <Button href={ getEventUrl(blogEntry) }>Læs om webinaret</Button>
                                            </div>
                                        }
                                        {
                                            (blogEntry.blog_type === 'Article' || blogEntry.blog_type === 'BLog') &&
                                            <div>
                                                <Button href={ Routes.Knowledge.Entry(blogEntry.slug) }>Læs artiklen</Button>
                                            </div>
                                        }

                                    </ContentBoxText>
                                    <ContentBoxFigure figure={ blogEntry.hero?.url } />
                                </ContentBoxContainer>
                                <Spacer />
                            </React.Fragment>
                        )) }
                    </>
                ) }
            </Query>



            <Spacer size="large" />


        </>
    );
}
